import { PreferenceService } from "../../services/preference.service";
import { ReportDashboardLayouts, ReportLayout, ReportWidgetDef } from "./report.types";

/** One user pref (type "U") holding every report dashboard's layout, keyed by dashboard id. */
export const REPORT_DASHBOARD_LAYOUTS_PREF = "reportDashboardLayouts";

export function defaultReportLayout(widgets: ReportWidgetDef[]): ReportLayout {
    return { order: widgets.map((widget) => widget.key), hidden: {} };
}

/** Parses the stored pref value (JSON string or already-parsed object). Anything unreadable yields `{}`. */
export function parseReportDashboardLayouts(raw: unknown): ReportDashboardLayouts {
    let parsed: unknown = raw;
    if (typeof raw === "string") {
        if (!raw.trim()) return {};
        try {
            parsed = JSON.parse(raw);
        } catch {
            return {};
        }
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return {};
    }
    return parsed as ReportDashboardLayouts;
}

/**
 * Reconciles a saved layout with the current widget list: drops widgets that no longer exist and
 * appends widgets added since the layout was saved so new features still show.
 */
export function normalizeReportLayout(
    saved: Partial<ReportLayout> | undefined,
    widgets: ReportWidgetDef[],
): ReportLayout {
    const knownKeys = new Set(widgets.map((widget) => widget.key));
    const savedOrder = Array.isArray(saved?.order) ? saved.order : [];
    const order = [...new Set(savedOrder.filter((key) => knownKeys.has(key)))];
    for (const widget of widgets) {
        if (!order.includes(widget.key)) {
            order.push(widget.key);
        }
    }
    const hidden: Record<string, boolean> = {};
    const savedHidden = saved?.hidden && typeof saved.hidden === "object" ? saved.hidden : {};
    for (const [widgetKey, isHidden] of Object.entries(savedHidden)) {
        if (knownKeys.has(widgetKey) && isHidden === true) {
            hidden[widgetKey] = true;
        }
    }
    return { order, hidden };
}

/** Replaces one dashboard's entry and keeps every other dashboard's layout intact. */
export function mergeReportDashboardLayouts(
    layouts: ReportDashboardLayouts,
    dashboardId: string,
    layout: ReportLayout,
): ReportDashboardLayouts {
    return { ...layouts, [dashboardId]: { order: [...layout.order], hidden: { ...layout.hidden } } };
}

export async function loadReportDashboardLayouts(): Promise<ReportDashboardLayouts> {
    const prefs = await PreferenceService.getPreferences([REPORT_DASHBOARD_LAYOUTS_PREF], "U");
    return parseReportDashboardLayouts(prefs?.[REPORT_DASHBOARD_LAYOUTS_PREF]?.value);
}

/** Re-reads the pref before writing so layouts saved by other dashboards (or tabs) are not clobbered. */
export async function saveReportDashboardLayout(dashboardId: string, layout: ReportLayout): Promise<void> {
    const current = await loadReportDashboardLayouts();
    const merged = mergeReportDashboardLayouts(current, dashboardId, layout);
    await PreferenceService.setPreference(REPORT_DASHBOARD_LAYOUTS_PREF, JSON.stringify(merged), "U");
}
